<template>
  <div class="flex h-full min-h-0 flex-col gap-3">
    <!-- Canvas toolbar -->
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex min-w-0 items-center gap-2 text-xs text-white/60">
        <UIcon name="i-heroicons-eye" class="h-4 w-4 shrink-0" />
        <span class="truncate font-medium text-white/85">{{ activePageTitle }}</span>
        <span
          v-if="interactionMode === 'edit'"
          class="rounded-full bg-blue-500/15 px-2 py-0.5 text-[10px] text-blue-300"
        >
          حالت ویرایش
        </span>
        <UBadge
          v-if="isDirty"
          label="تغییرات ذخیره نشده"
          color="warning"
          variant="soft"
          size="xs"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <UButton
          v-if="interactionMode === 'edit' && !isSheet"
          size="xs"
          color="primary"
          variant="solid"
          icon="i-heroicons-plus"
          @click="emit('add-block')"
        >
          افزودن بلوک
        </UButton>
        <div class="flex items-center rounded-lg border border-white/10 bg-white/5 p-0.5">
          <UTooltip text="پیش‌نمایش دسکتاپ">
            <UButton
              :size="isSheet ? 'sm' : 'xs'"
              :variant="deviceMode === 'desktop' ? 'solid' : 'ghost'"
              :color="deviceMode === 'desktop' ? 'primary' : 'neutral'"
              icon="i-heroicons-computer-desktop"
              aria-label="پیش‌نمایش دسکتاپ"
              @click="setDeviceMode('desktop')"
            />
          </UTooltip>
          <UTooltip text="پیش‌نمایش موبایل">
            <UButton
              :size="isSheet ? 'sm' : 'xs'"
              :variant="deviceMode === 'mobile' ? 'solid' : 'ghost'"
              :color="deviceMode === 'mobile' ? 'primary' : 'neutral'"
              icon="i-heroicons-device-phone-mobile"
              aria-label="پیش‌نمایش موبایل"
              @click="setDeviceMode('mobile')"
            />
          </UTooltip>
        </div>
      </div>
    </div>

    <!-- Preview frame -->
    <div
      ref="frameHost"
      class="flex min-h-0 flex-1 pb-2"
      :class="frameHostClass"
    >
      <!--
        dir=ltr keeps the scaled 960px frame pinned to the physical left of the
        sizer so RTL on the builder shell cannot push the layout box off-screen.
        Site content still uses previewDir on .preview-viewport.
      -->
      <div
        class="preview-scale-sizer"
        dir="ltr"
        :style="sizerStyle"
      >
        <div
          ref="previewInner"
          class="preview-frame transition-all duration-300 ease-out"
          :class="previewFrameClass"
          :style="frameStyle"
        >
        <!-- Browser chrome (desktop, split layout only) -->
        <div
          v-if="deviceMode === 'desktop' && !isSheet"
          class="flex items-center gap-2 rounded-t-xl border border-b-0 border-white/10 bg-[#1e2128] px-3 py-2"
        >
          <span class="size-2.5 rounded-full bg-rose-400/70" />
          <span class="size-2.5 rounded-full bg-amber-300/70" />
          <span class="size-2.5 rounded-full bg-emerald-400/70" />
          <div class="ms-2 flex flex-1 items-center gap-2 truncate rounded-md bg-black/30 px-3 py-1 text-[10px] text-white/55">
            <img
              v-if="faviconUrl"
              :src="faviconUrl"
              alt="favicon"
              class="size-3.5 rounded-sm bg-white object-contain p-px"
            >
            <UIcon
              v-else
              name="i-heroicons-globe-alt"
              class="size-3 text-white/40"
            />
            <span class="truncate">preview.yoursite.com{{ activePageSlugPath }}</span>
          </div>
          <UIcon name="i-heroicons-arrow-path" class="size-3.5 text-white/30" />
        </div>

        <!-- Phone chrome (mobile, split layout only) -->
        <div
          v-else-if="!isSheet"
          class="mx-auto flex w-full justify-center rounded-t-[2rem] border border-b-0 border-white/10 bg-[#1e2128] px-4 pt-3 pb-1"
        >
          <div class="h-1 w-16 rounded-full bg-white/20" />
        </div>

        <!-- Site preview -->
        <div
          class="preview-viewport relative overflow-hidden bg-default text-default"
          :class="previewViewportClass"
          :style="themeStyle"
          :dir="previewDir"
        >
          <div
            ref="scrollContainer"
            class="preview-scroll overflow-y-auto overflow-x-hidden"
            :class="previewScrollClass"
          >
            <div
              v-if="!activePageEnabled"
              class="flex flex-col items-center justify-center gap-3 px-6 py-24 text-center"
            >
              <UIcon name="i-heroicons-eye-slash" class="size-10 text-gray-400" />
              <p class="text-sm text-gray-500">
                این صفحه غیرفعال است. از پنل راست آن را فعال کنید.
              </p>
            </div>

            <template v-else>
              <!-- Header is always first — nothing above it -->
              <template v-for="(block, index) in headerOnlyBlocks" :key="'header-' + index">
                <div
                  v-if="block.enabled !== false"
                  class="relative builder-block-shell"
                  :class="layoutShellClass(headerIndex(index))"
                  @click.stop="onLayoutBlockClick(headerIndex(index))"
                >
                  <div
                    v-if="interactionMode === 'edit'"
                    class="pointer-events-none absolute inset-0 z-10"
                    :class="selectedScope === 'layout' && selectedBlockIndex === headerIndex(index)
                      ? 'ring-2 ring-blue-500 ring-inset'
                      : 'builder-block-shell-idle'"
                  />
                  <div
                    v-if="interactionMode === 'edit' && selectedScope === 'layout' && selectedBlockIndex === headerIndex(index)"
                    class="pointer-events-none absolute end-2 top-2 z-20 rounded-md bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white shadow"
                  >
                    {{ blockLabelOf(block.component) }}
                  </div>
                  <div :class="interactionMode === 'edit' ? 'builder-edit-content' : ''">
                    <BlockRenderer :block="wrapBlockProps(block, headerIndex(index), 'layout')" />
                  </div>
                </div>
              </template>

              <!-- Banner / mid layout chrome sits below the header -->
              <template v-for="(block, index) in midLayoutBlocks" :key="'mid-' + index">
                <div
                  v-if="block.enabled !== false"
                  class="relative builder-block-shell"
                  :class="layoutShellClass(midIndex(index))"
                  @click.stop="onLayoutBlockClick(midIndex(index))"
                >
                  <div
                    v-if="interactionMode === 'edit'"
                    class="pointer-events-none absolute inset-0 z-10"
                    :class="selectedScope === 'layout' && selectedBlockIndex === midIndex(index)
                      ? 'ring-2 ring-blue-500 ring-inset'
                      : 'builder-block-shell-idle'"
                  />
                  <div
                    v-if="interactionMode === 'edit' && selectedScope === 'layout' && selectedBlockIndex === midIndex(index)"
                    class="pointer-events-none absolute end-2 top-2 z-20 rounded-md bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white shadow"
                  >
                    {{ blockLabelOf(block.component) }}
                  </div>
                  <div :class="interactionMode === 'edit' ? 'builder-edit-content' : ''">
                    <BlockRenderer :block="wrapBlockProps(block, midIndex(index), 'layout')" />
                  </div>
                </div>
              </template>

              <!-- Page blocks -->
              <template v-for="(block, index) in pageBlocks" :key="'page-' + index">
                <div
                  v-if="block.enabled !== false"
                  class="relative builder-block-shell"
                  :class="pageShellClass(index)"
                  @click.stop="onPageBlockClick(index)"
                >
                  <div
                    v-if="interactionMode === 'edit'"
                    class="pointer-events-none absolute inset-0 z-10"
                    :class="selectedScope === 'page' && selectedBlockIndex === index
                      ? 'ring-2 ring-blue-500 ring-inset'
                      : 'builder-block-shell-idle'"
                  />
                  <div
                    v-if="interactionMode === 'edit' && selectedScope === 'page' && selectedBlockIndex === index"
                    class="pointer-events-none absolute end-2 top-2 z-20 rounded-md bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white shadow"
                  >
                    {{ blockLabelOf(block.component) }}
                  </div>
                  <div :class="interactionMode === 'edit' ? 'builder-edit-content' : ''">
                    <BlockRenderer :block="wrapBlockProps(block, index, 'page')" />
                  </div>
                </div>
              </template>

              <!-- Footer is always last — nothing below it -->
              <template v-for="(block, index) in footerOnlyBlocks" :key="'footer-' + index">
                <div
                  v-if="block.enabled !== false"
                  class="relative builder-block-shell"
                  :class="layoutShellClass(footerIndex(index))"
                  @click.stop="onLayoutBlockClick(footerIndex(index))"
                >
                  <div
                    v-if="interactionMode === 'edit'"
                    class="pointer-events-none absolute inset-0 z-10"
                    :class="selectedScope === 'layout' && selectedBlockIndex === footerIndex(index)
                      ? 'ring-2 ring-blue-500 ring-inset'
                      : 'builder-block-shell-idle'"
                  />
                  <div
                    v-if="interactionMode === 'edit' && selectedScope === 'layout' && selectedBlockIndex === footerIndex(index)"
                    class="pointer-events-none absolute end-2 top-2 z-20 rounded-md bg-blue-600 px-2 py-0.5 text-[10px] font-medium text-white shadow"
                  >
                    {{ blockLabelOf(block.component) }}
                  </div>
                  <div :class="interactionMode === 'edit' ? 'builder-edit-content' : ''">
                    <BlockRenderer :block="wrapBlockProps(block, footerIndex(index), 'layout')" />
                  </div>
                </div>
              </template>

              <button
                v-if="rawPageBlocks.length === 0 && interactionMode === 'edit'"
                type="button"
                class="m-6 flex w-[calc(100%-3rem)] flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-gray-300 px-6 py-16 text-center text-gray-500 transition hover:border-primary-500/60 hover:bg-primary-500/5 hover:text-primary-700 dark:border-gray-700 dark:text-gray-400"
                @click="emit('add-block')"
              >
                <span class="flex size-12 items-center justify-center rounded-full bg-primary-500/10 text-primary-600 dark:text-primary-400">
                  <UIcon name="i-heroicons-squares-plus" class="size-6" />
                </span>
                <span class="text-sm font-semibold">
                  این صفحه خالی است
                </span>
                <span class="text-xs">
                  برای افزودن اولین بلوک کلیک کنید
                </span>
              </button>

              <div
                v-else-if="rawPageBlocks.length === 0 && interactionMode === 'browse'"
                class="px-6 py-20 text-center text-sm text-gray-500"
              >
                هنوز بلوکی برای این صفحه فعال نیست.
              </div>
            </template>
          </div>
        </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import BlockRenderer from '~/components/BlockRenderer.vue'
import { blockLabel as getBlockLabel, isRtlLocale } from '~/composables/useBuilderMeta'
import { DEFAULT_FONT_FAMILY, fontStack } from '~/composables/useBuilderMeta'
import { themeCssVarsFromPalette } from '~/composables/useThemeCssVars'
import { useBuilderLayout } from '~/composables/useBuilderLayout'
import { useBuilderStore } from '~/stores/useBuilderStore'
import type { Block } from '~/stores/useBuilderStore'
import { resolveLocalized } from '~/utils/resolveLocalized'
import {
  isLayoutFooter,
  isLayoutHeader,
  isLayoutMidChrome,
} from '~/utils/layoutChrome'

const emit = defineEmits<{
  (e: 'add-block'): void
}>()

const builderStore = useBuilderStore()
const { isSheet } = useBuilderLayout()

const deviceMode = computed(() => builderStore.deviceMode)
const interactionMode = computed(() => builderStore.interactionMode)
const selectedBlockIndex = computed(() => builderStore.selectedBlockIndex)
const selectedScope = computed(() => builderStore.selectedScope)
const editLocale = computed(() => builderStore.editLocale)
const isDirty = computed(() => builderStore.isDirty)

const DESKTOP_FRAME_WIDTH = 960

const frameHost = ref<HTMLElement | null>(null)
const previewInner = ref<HTMLElement | null>(null)
const desktopScale = ref(1)
const scaledHeight = ref(0)

const scaleDesktop = computed(() => isSheet.value && deviceMode.value === 'desktop')

const frameHostClass = computed(() => {
  if (isSheet.value && deviceMode.value === 'mobile') {
    return 'items-stretch justify-stretch overflow-hidden'
  }
  if (scaleDesktop.value) {
    // Layout box matches the scaled visual — never allow horizontal scroll.
    return 'items-start justify-center overflow-x-hidden overflow-y-auto'
  }
  return 'items-start justify-center overflow-auto'
})

const previewFrameClass = computed(() => {
  if (isSheet.value && deviceMode.value === 'mobile') return 'preview-frame--native'
  if (scaleDesktop.value) return 'preview-frame--desktop preview-frame--scaled'
  return deviceMode.value === 'mobile' ? 'preview-frame--mobile' : 'preview-frame--desktop'
})

const previewViewportClass = computed(() => {
  if (isSheet.value) return 'rounded-none'
  return deviceMode.value === 'mobile' ? 'rounded-b-[2rem]' : 'rounded-b-xl'
})

const previewScrollClass = computed(() => {
  if (isSheet.value && deviceMode.value === 'mobile') return 'h-full max-h-none'
  if (scaleDesktop.value) return 'max-h-none'
  return deviceMode.value === 'mobile' ? 'max-h-[min(68vh,640px)]' : 'max-h-[min(72vh,780px)]'
})

const sizerStyle = computed(() => {
  if (!scaleDesktop.value) return isSheet.value ? { width: '100%', height: '100%' } : undefined
  // Width must match the *visual* scaled size; transform alone leaves a 960px layout box.
  return {
    width: `${DESKTOP_FRAME_WIDTH * desktopScale.value}px`,
    maxWidth: '100%',
    height: `${scaledHeight.value || 1}px`,
    overflow: 'hidden',
  }
})

const frameStyle = computed(() => {
  if (!scaleDesktop.value) {
    return isSheet.value && deviceMode.value === 'mobile'
      ? { width: '100%', height: '100%' }
      : undefined
  }
  return {
    width: `${DESKTOP_FRAME_WIDTH}px`,
    transform: `scale(${desktopScale.value})`,
    transformOrigin: 'top left',
  }
})

function updateDesktopScale() {
  if (!scaleDesktop.value || !frameHost.value || !previewInner.value) {
    desktopScale.value = 1
    scaledHeight.value = 0
    return
  }
  const available = frameHost.value.clientWidth
  const s = Math.min(1, available / DESKTOP_FRAME_WIDTH)
  desktopScale.value = s
  const innerHeight = previewInner.value.scrollHeight || previewInner.value.offsetHeight
  scaledHeight.value = innerHeight * s
  // Drop any leftover horizontal scroll from the previous (broken) layout box.
  frameHost.value.scrollLeft = 0
}

const siteData = computed(() => builderStore.siteData)
const activePage = computed(() => builderStore.activePage)

const activePageEnabled = computed(() => activePage.value?.enabled !== false)
const activePageTitle = computed(
  () =>
    activePage.value?.title?.[editLocale.value] ??
    activePage.value?.title?.[builderStore.defaultLocale] ??
    'صفحه',
)
const activePageSlugPath = computed(() => activePage.value?.slug ?? '/')
const faviconUrl = computed(() => siteData.value?.siteSettings.favicon ?? '')

const previewDir = computed(() => (isRtlLocale(editLocale.value) ? 'rtl' : 'ltr'))

const colorMode = useColorMode()
const themeSurfaceMode = computed(() => (colorMode.value === 'dark' ? 'dark' : 'light'))

const themeStyle = computed(() => {
  const primary = siteData.value?.siteSettings.themePrimary ?? 'blue'
  const neutral = siteData.value?.siteSettings.themeNeutral ?? 'slate'
  return {
    ...themeCssVarsFromPalette(primary, neutral, themeSurfaceMode.value),
    fontFamily: fontStack(siteData.value?.siteSettings.fontFamily ?? DEFAULT_FONT_FAMILY),
  }
})

const rawLayoutBlocks = computed(() => siteData.value?.layout?.blocks ?? [])
const rawPageBlocks = computed(() => {
  const blocks = activePage.value?.blocks
  return Array.isArray(blocks) ? blocks : []
})

const siteLocales = computed(() => siteData.value?.i18n?.locales ?? ['fa', 'en'])

const layoutBlocks = computed(() =>
  rawLayoutBlocks.value.map((block) => localizeBlock(block)),
)
const pageBlocks = computed(() =>
  rawPageBlocks.value.map((block) => localizeBlock(block)),
)

/** Header always first. */
const headerOnlyBlocks = computed(() =>
  layoutBlocks.value.filter((b) => isLayoutHeader(b.component)),
)
/** Banner and other mid chrome sit below the header. */
const midLayoutBlocks = computed(() =>
  layoutBlocks.value.filter((b) => isLayoutMidChrome(b.component)),
)
/** Footer always last. */
const footerOnlyBlocks = computed(() =>
  layoutBlocks.value.filter((b) => isLayoutFooter(b.component)),
)

function indexOfNthMatching(
  predicate: (component: string) => boolean,
  filteredIndex: number,
): number {
  let count = 0
  for (let i = 0; i < rawLayoutBlocks.value.length; i++) {
    const block = rawLayoutBlocks.value[i]
    if (!block || !predicate(block.component)) continue
    if (count === filteredIndex) return i
    count++
  }
  return -1
}

function headerIndex(filteredIndex: number): number {
  return indexOfNthMatching(isLayoutHeader, filteredIndex)
}

function midIndex(filteredIndex: number): number {
  return indexOfNthMatching(isLayoutMidChrome, filteredIndex)
}

function footerIndex(filteredIndex: number): number {
  return indexOfNthMatching(isLayoutFooter, filteredIndex)
}

function localizeBlock(block: Block) {
  try {
    return {
      ...block,
      props: resolveLocalized(
        block.props,
        editLocale.value,
        siteLocales.value,
        builderStore.defaultLocale,
      ) as Record<string, any>,
    }
  } catch (err) {
    console.error('[builder] Failed to localize block', block.component, err)
    return { ...block, props: { ...(block.props ?? {}) } }
  }
}

function setDeviceMode(mode: 'desktop' | 'mobile') {
  builderStore.setDeviceMode(mode)
}

function blockLabelOf(component: string) {
  return getBlockLabel(component, editLocale.value)
}

function pageShellClass(index: number) {
  if (interactionMode.value !== 'edit') return ''
  return [
    'cursor-pointer transition-shadow',
    selectedScope.value === 'page' && selectedBlockIndex.value === index ? 'z-[1]' : '',
  ]
}

function layoutShellClass(index: number) {
  if (interactionMode.value !== 'edit') return ''
  return [
    'cursor-pointer transition-shadow',
    selectedScope.value === 'layout' && selectedBlockIndex.value === index ? 'z-[1]' : '',
  ]
}

function onPageBlockClick(index: number) {
  if (interactionMode.value !== 'edit') return
  builderStore.selectBlock(index, 'page')
  if (isSheet.value) builderStore.setSheetSnap('half')
}

function onLayoutBlockClick(index: number) {
  if (interactionMode.value !== 'edit') return
  if (index < 0) return
  builderStore.selectBlock(index, 'layout')
  if (isSheet.value) builderStore.setSheetSnap('half')
}

function wrapBlockProps(
  block: Block & { props: Record<string, any> },
  _index: number,
  _scope: 'page' | 'layout',
) {
  const props = { ...block.props }
  const navigate = (to: string) => builderStore.navigateInPreview(to)

  if (block.component === 'BlockHeader') {
    const menuItems = builderStore.getMenuItemsForPreview()
    return {
      ...block,
      props: {
        ...props,
        menuItems,
        // Always preview-navigate inside the builder (edit + browse).
        builderPreview: true,
        previewDevice: deviceMode.value,
        onNavigate: navigate,
        previewNavigate: navigate,
      },
    }
  }

  return {
    ...block,
    props: {
      ...props,
      builderPreview: true,
      previewDevice: deviceMode.value,
      onNavigate: navigate,
      previewNavigate: navigate,
    },
  }
}

let scaleObserver: ResizeObserver | null = null

onMounted(() => {
  if (!import.meta.client) return
  scaleObserver = new ResizeObserver(() => {
    void nextTick(updateDesktopScale)
  })
  if (frameHost.value) scaleObserver.observe(frameHost.value)
  if (previewInner.value) scaleObserver.observe(previewInner.value)
  updateDesktopScale()
})

watch(
  [scaleDesktop, deviceMode, isSheet, () => builderStore.activePageSlug, () => builderStore.siteData],
  () => {
    void nextTick(() => {
      if (scaleObserver && previewInner.value) {
        scaleObserver.disconnect()
        if (frameHost.value) scaleObserver.observe(frameHost.value)
        if (previewInner.value) scaleObserver.observe(previewInner.value)
      }
      updateDesktopScale()
    })
  },
)

onBeforeUnmount(() => {
  scaleObserver?.disconnect()
})
</script>

<style scoped>
.preview-frame--desktop {
  width: 100%;
  max-width: 960px;
}

.preview-frame--scaled {
  /* Width comes from inline style (960px); sizer clips to the scaled visual. */
  max-width: none;
}

.preview-frame--mobile {
  width: 390px;
  max-width: 100%;
}

.preview-frame--native {
  width: 100%;
  max-width: 100%;
  height: 100%;
  box-shadow: none;
}

.preview-frame--native .preview-viewport,
.preview-frame--native .preview-scroll {
  height: 100%;
}

.preview-scale-sizer {
  min-width: 0;
  flex-shrink: 0;
}

.preview-frame {
  box-shadow:
    0 0 0 1px rgb(255 255 255 / 0.08),
    0 24px 48px -12px rgb(0 0 0 / 0.5);
}

.preview-frame--native {
  box-shadow: none;
}

.builder-block-shell-idle {
  /* hover highlight is pointer-driven; coarse devices get a persistent hint */
}

@media (hover: hover) {
  .builder-block-shell-idle {
    transition: box-shadow 150ms ease;
  }
  .builder-block-shell:hover .builder-block-shell-idle {
    box-shadow: inset 0 0 0 1px rgb(96 165 250 / 0.4);
  }
}

@media (hover: none) {
  .builder-block-shell-idle {
    box-shadow: inset 0 0 0 1px rgb(96 165 250 / 0.22);
  }
}

.preview-viewport :deep([data-slot='container']) {
  max-width: 100% !important;
  min-width: 0;
}

.preview-viewport :deep(section [data-slot='container']) {
  grid-template-columns: minmax(0, 1fr) !important;
}

.preview-viewport :deep(.swiper) {
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.preview-viewport :deep([class*='text-primary-']) {
  color: var(--ui-primary) !important;
}
.preview-viewport :deep([class*='border-primary-']) {
  border-color: color-mix(in srgb, var(--ui-primary) 100%, transparent) !important;
}
.preview-viewport :deep([class*='ring-primary-']) {
  --tw-ring-color: color-mix(in srgb, var(--ui-primary) 100%, transparent) !important;
}
.preview-viewport :deep([class*='from-primary-']) {
  --tw-gradient-from: var(--ui-primary) !important;
}
.preview-viewport :deep([class*='via-primary-']) {
  --tw-gradient-via: var(--ui-primary) !important;
}
.preview-viewport :deep([class*='to-primary-']) {
  --tw-gradient-to: var(--ui-primary) !important;
}
.preview-viewport :deep([class*='shadow-primary-']) {
  --tw-shadow-color: color-mix(in srgb, var(--ui-primary) 35%, transparent) !important;
}
.preview-viewport :deep([class*='bg-primary-']:not([class*='bg-primary-0'])) {
  /* fallback so any bg-primary-* gets a tinted color in preview */
}

/* Edit mode: keep block selection, but allow CTA/link clicks inside the preview. */
.builder-edit-content :deep(a),
.builder-edit-content :deep(button),
.builder-edit-content :deep([role='button']),
.builder-edit-content :deep(input),
.builder-edit-content :deep(textarea),
.builder-edit-content :deep(select),
.builder-edit-content :deep(label) {
  position: relative;
  z-index: 15;
}
</style>
